import React, { useState, useEffect } from 'react';
import { 
  Checkbox, 
  Card, 
  Flex, 
  Text, 
  Title, 
  Badge, 
  Group, 
  Stack,
  Box,
  Menu,
  UnstyledButton,
  Avatar,
  Tooltip,
  ActionIcon
} from '@mantine/core';
import { Bell, CheckCircle } from 'lucide-react';
import type { TodoItem as TodoItemType } from '../types/todo';
import { useToggleTodo, useUpdateTodo, useTodoLists, useUsers } from '../hooks/useTodos';
import { InlineDatePicker } from './InlineDatePicker';
import { Comments } from './Comments';
import { formatCompletionDate } from '../utils/dateUtils';
import { useUser } from '../contexts/UserContext';
import { api } from '../api';
import { TagDisplay } from './TagDisplay';
import { SearchHighlight } from './SearchHighlight';
import { useKanbanConfig } from '../hooks/useKanbanConfig';
import { SearchHighlightWithLinks } from '../utils/linkUtils';

import type { SortType } from './SortDropdownNew';
import type { DraggableAttributes } from '@dnd-kit/core';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';

interface TodoItemProps {
  todo: TodoItemType;
  onEdit?: (todo: TodoItemType) => void;
  isHighlighted?: boolean;
  onInteraction?: () => void;
  highlightMessage?: string;
  listFilter?: string | number;
  dragHandleProps?: Record<string, unknown> | null;
  dragAttributes?: DraggableAttributes | null;
  dragListeners?: SyntheticListenerMap | null;
  onUpdated?: (id: number, message?: string) => void;
  sortType?: SortType;
  isPublicView?: boolean;
  searchTerm?: string;
}

import DragHandle from './DragHandle';
import './TodoItem.css';

const TodoItem: React.FC<TodoItemProps> = ({ 
  todo, 
  onEdit, 
  isHighlighted = false, 
  onInteraction,
  highlightMessage,
  listFilter,
  dragHandleProps,
  dragAttributes,
  dragListeners,
  onUpdated,
  sortType,
  isPublicView = false,
  searchTerm
}) => {
  const [localComments, setLocalComments] = useState(todo.comments || []);
  
  // Completion animation states
  const [isCompleting, setIsCompleting] = useState(false);
  const [justCompleted, setJustCompleted] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  
  // Scroll to the highlighted todo item
  useEffect(() => {
    if (isHighlighted) {
      // Scroll functionality can be added here if needed
    }
  }, [todo.id, todo.title, isHighlighted, highlightMessage]);
  
  const toggleMutation = useToggleTodo();
  const updateMutation = useUpdateTodo();
  const { currentUser } = useUser();
  const { data: users = [] } = useUsers();


  // Update local comments when todo changes, but preserve optimistic updates
  useEffect(() => {
    
    // Always update from server data to ensure consistency
    const serverComments = todo.comments || [];
    setLocalComments(serverComments);
  }, [todo.comments, todo.id]);

  const handleAddComment = async (text: string) => {
    if (!currentUser?.email) {
      console.error('No current user');
      return;
    }

    try {
      const newComment = await api.addComment(todo.id, text, currentUser.email);
      
      // Optimistic update for immediate feedback
      setLocalComments(prev => [...prev, newComment]);
      
      // The SignalR update will overwrite this when it arrives via the useEffect
    } catch (error) {
      console.error('Failed to add comment:', error);
      throw error; // Re-throw so the Comments component can handle it
    }
  };

  const handleToggle = async () => {
    // If already completing, prevent double-toggle
    if (isCompleting) return;
    
    // If completing a todo (checking it off)
    if (!todo.isCompleted) {
      // Start completion animation
      setIsCompleting(true);
      setJustCompleted(true);
      
      // Show celebration effect for 1.2 seconds
      setTimeout(() => {
        setIsAnimatingOut(true);
        
        // After fade starts, actually toggle the todo
        setTimeout(() => {
          toggleMutation.mutate(todo.id);
          onInteraction?.(); // Clear highlight on interaction
          
          // Update status to [done] column if one exists
          if (kanbanConfig?.columns) {
            const doneColumn = kanbanConfig.columns.find(col => col.type === 'done');
            if (doneColumn && currentUser?.email) {
              updateMutation.mutate({
                id: todo.id,
                data: {
                  status: doneColumn.id
                }
              });
            }
          }
          
          // Reset animation states after mutation
          setTimeout(() => {
            setIsCompleting(false);
            setJustCompleted(false);
            setIsAnimatingOut(false);
          }, 300);
        }, 300); // 300ms fade duration
      }, 1200); // 1.2s celebration duration
    } else {
      // If unchecking a completed todo, do it immediately
      toggleMutation.mutate(todo.id);
      onInteraction?.(); // Clear highlight on interaction
      // When unchecking a completed todo, keep it in the [done] column
      // Don't change the status - let it stay where it is
    }
  };


  const handleEdit = () => {
    if (onEdit) {
      onEdit(todo);
      onInteraction?.(); // Clear highlight on interaction
    }
  };

  // Use a global storage key unique to this todo to track date changes across component remounts
  const dateStorageKey = `todo-${todo.id}-prev-date`;
  const initializedKey = `todo-${todo.id}-initialized`;

  // Get previous date from storage
  const getPrevDate = () => {
    try {
      return localStorage.getItem(dateStorageKey);
    } catch {
      return null;
    }
  };

  // Set previous date in storage
  const setPrevDate = (date: string | null) => {
    try {
      if (date) {
        localStorage.setItem(dateStorageKey, date);
      } else {
        localStorage.removeItem(dateStorageKey);
      }
    } catch {
      // Ignore storage errors
    }
  };

  // Check if this todo has been initialized in storage
  const isInitialized = () => {
    try {
      return localStorage.getItem(initializedKey) === 'true';
    } catch {
      return false;
    }
  };

  // Mark this todo as initialized
  const markAsInitialized = () => {
    try {
      localStorage.setItem(initializedKey, 'true');
    } catch {
      // Ignore storage errors
    }
  };

  // Normalize date strings for comparison (handle different formats like '2025-07-16' vs '2025-07-16T00:00:00Z')
  const normalizeDateString = (dateStr: string | null | undefined): string | null => {
    if (!dateStr) return null;
    try {
      // Parse and convert to ISO date string to normalize format
      return new Date(dateStr).toISOString();
    } catch {
      return dateStr;
    }
  };

  // Initialize storage on first render - DON'T trigger highlighting for initial setup
  useEffect(() => {
    if (!isInitialized()) {
      setPrevDate(todo.dueDate || null);
      markAsInitialized();
    }
  }, [todo.id]);

  // Only track due date changes AFTER initialization - prevents false highlights on app startup
  useEffect(() => {
    // Skip if not initialized yet
    if (!isInitialized()) {
      return;
    }

    const normalizedCurrent = normalizeDateString(todo.dueDate);
    const prevDate = getPrevDate();
    const normalizedPrev = normalizeDateString(prevDate);
    
    // Only trigger highlighting if there's an actual change AND the todo has been initialized
    if (normalizedCurrent !== normalizedPrev && onUpdated) {
      console.log(`User-initiated date change detected for todo ${todo.id}: ${normalizedPrev} -> ${normalizedCurrent}`);
      
      const message = todo.dueDate ? 'DUE DATE UPDATED' : 'DUE DATE REMOVED';
      onUpdated(todo.id, message);
    }
    
    // Always update storage to track the current state
    setPrevDate(todo.dueDate || null);
  }, [todo.dueDate, onUpdated, todo.id]);

  const handleDateChange = (newDate: string | undefined) => {
    const currentNormalized = normalizeDateString(todo.dueDate);
    const newNormalized = normalizeDateString(newDate);
    
    // Trigger highlight immediately for any actual date change from user interaction
    if (onUpdated && currentNormalized !== newNormalized) {
      const message = newDate === undefined ? 'DUE DATE REMOVED' : 'DUE DATE UPDATED';
      console.log(`User date change in handleDateChange: ${currentNormalized} -> ${newNormalized}`);
      onUpdated(todo.id, message);
      
      // Pre-update localStorage to prevent useEffect from triggering duplicate highlighting
      setPrevDate(newDate || null);
    }
    
    updateMutation.mutate({
      id: todo.id,
      data: {
        dueDate: newDate === undefined ? null : newDate // Explicitly send null when clearing
      }
    });
  };

  const handlePriorityChange = (newPriority: 'Low' | 'Medium' | 'High') => {
    updateMutation.mutate({
      id: todo.id,
      data: {
        priority: newPriority
      }
    }, {
      onSuccess: () => {
        if (onUpdated) onUpdated(todo.id, 'PRIORITY UPDATED');
      }
    });
  };

  const handleAssigneeChange = (newAssignee: string) => {
    const assigneeValue = newAssignee === '__unassigned__' ? '' : newAssignee;
    updateMutation.mutate({
      id: todo.id,
      data: {
        assignedTo: assigneeValue
      }
    }, {
      onSuccess: () => {
        if (onUpdated) onUpdated(todo.id, 'Assignee changed!');
      }
    });
  };

  const handleListChange = (newListId: number) => {
    updateMutation.mutate({
      id: todo.id,
      data: {
        listId: newListId
      }
    }, {
      onSuccess: () => {
        if (onUpdated) onUpdated(todo.id, 'Moved to different list!');
      }
    });
  };

  // Simplified click handlers

  const handleDatePickerClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the edit modal
  };

  // Removed CSS-related utility functions - using pure Mantine styling now

  // Get all lists for color lookup
  const { data: todoLists = [] } = useTodoLists();
  const list = todoLists.find((l: any) => l.id === todo.listId);
  const listColor = list?.color || '#e5e7eb';

  // Get Kanban config for status badge
  const { data: kanbanConfig } = useKanbanConfig(todo.listId);

  // Hide list label unless "All lists" is selected in the filter.
  const hideListLabel = typeof listFilter !== 'undefined' && listFilter !== 'all';

  return (
    <Box 
      style={{ 
        position: 'relative',
        transform: justCompleted ? 'scale(1.02)' : 'scale(1)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        opacity: isAnimatingOut ? 0 : 1
      }} 
      data-task-id={todo.id}
    >
      
      {/* Completion celebration overlay */}
      {justCompleted && (
        <Box
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 20,
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'completion-bounce 0.6s ease-out'
          }}
        >
          <CheckCircle 
            size={48} 
            style={{
              color: 'var(--mantine-color-green-6)',
              filter: 'drop-shadow(0 0 8px rgba(34, 197, 94, 0.5))',
              animation: 'completion-pulse 1.2s ease-out'
            }}
          />
        </Box>
      )}
      
      {/* Highlight message label - positioned to hover above the card */}
      {isHighlighted && highlightMessage && (
        <Box
          style={{
            position: 'absolute',
            top: '-16px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Badge 
            variant="gradient" 
            gradient={{ from: 'blue', to: 'cyan' }}
            size="sm"
            style={{
              fontSize: '0.7rem',
              fontWeight: 700,
              padding: '4px 12px',
              borderRadius: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
              boxShadow: '0 2px 6px rgba(59, 130, 246, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <span style={{ fontSize: '12px' }}>📍</span>
            {highlightMessage}
          </Badge>
        </Box>
      )}
      
      <Card
        shadow="sm"
        padding="xs"
        radius="md"
        withBorder
        style={{
          opacity: todo.isCompleted ? 0.7 : 1,
          border: isHighlighted ? '2px solid var(--mantine-color-blue-4)' : justCompleted ? '2px solid var(--mantine-color-green-4)' : undefined,
          backgroundColor: isHighlighted ? 'rgba(59, 130, 246, 0.04)' : justCompleted ? 'rgba(34, 197, 94, 0.08)' : 'var(--app-surface)',
          boxShadow: isHighlighted ? '0 6px 20px rgba(59, 130, 246, 0.12), 0 0 0 1px rgba(59, 130, 246, 0.1)' : 
                     justCompleted ? '0 6px 30px rgba(34, 197, 94, 0.2), 0 0 0 1px rgba(34, 197, 94, 0.3)' : undefined,
          transform: isHighlighted ? 'translateY(-1px)' : undefined,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          marginTop: isHighlighted && highlightMessage ? '20px' : '0'
        }}
        onClick={() => onInteraction?.()}
        className="todo-item-card"
      >
        <Flex direction="column" gap={0}>
          {/* Top row: checkbox, content, actions - with positioning context */}
          <Flex align="center" gap="sm" style={{ position: 'relative', width: '100%' }}>
            
            {/* Show drag handle when sorting by rank (both desktop and mobile) */}
            {sortType === 'rank' && (
              <div style={{ flexShrink: 0, alignSelf: 'center', display: 'flex', alignItems: 'center' }}>
                <DragHandle 
                  provided={{ dragHandleProps }}
                  attributes={dragAttributes}
                  listeners={dragListeners}
                />
              </div>
            )}
            
            {/* Always show checkbox, compact on mobile */}
            <Checkbox
              checked={todo.isCompleted}
              onChange={(e) => {
                e.stopPropagation();
                if (!isPublicView) {
                  handleToggle();
                }
              }}
              disabled={isPublicView || toggleMutation.isPending}
              size="lg"
              radius="md"
              style={{ 
                flexShrink: 0, 
                flexGrow: 0,
                alignSelf: 'center'
              }}
              styles={{
                root: {
                  minHeight: 'unset !important',
                  height: 'auto !important'
                },
                input: {
                  flexShrink: '0 !important',
                  minHeight: 'unset !important',
                  height: 'auto !important'
                }
              }}
            />

            <Stack 
              gap={0}
              style={{ 
                flex: 1, 
                minWidth: 0
              }}
              onClick={isPublicView ? undefined : handleEdit}
              title={todo.id < 0 ? "Task is being created..." : undefined}
            >
              <div
                style={{
                  minWidth: 0
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', rowGap: 0 }}>
                  <Title
                    order={6}
                    size="lg"
                    fw={500}
                    style={{
                      textDecoration: todo.isCompleted ? 'line-through' : 'none',
                      opacity: todo.isCompleted ? 0.6 : 1,
                      textAlign: 'left',
                      margin: 0,
                      flex: '1 1 auto',
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word'
                    }}
                  >
                    <SearchHighlight text={todo.title} searchTerm={searchTerm || ''} />
                  </Title>
                  {/* Display tags behind title */}
                  <TagDisplay 
                    tags={todo.tags} 
                    onTagClick={isPublicView ? undefined : handleEdit}
                    maxVisible={4}
                    size="xs"
                  />
                </div>
              </div>
              
              {todo.description && todo.description.trim() && (
                <Text size="sm" c="dimmed" style={{ textAlign: 'left', marginTop: '1px', marginBottom: 0, lineHeight: 1.2, whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>
                  <SearchHighlightWithLinks text={todo.description} searchTerm={searchTerm || ''} />
                </Text>
              )}
              
              {/* Metadata row */}
              <Group gap="xs" wrap="wrap" align="center" className="todo-item-metadata" style={{ marginTop: todo.description?.trim() ? '1px' : '2px' }}>
                {/* List chooser */}
                {!hideListLabel && !isPublicView && (
                  <Menu shadow="md" width={200}>
                    <Menu.Target>
                      <UnstyledButton
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Badge
                          variant="outline"
                          size="sm"
                          color="gray"
                          title={todo.listName}
                          leftSection={
                            <Box
                              style={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                backgroundColor: listColor,
                                marginRight: 4
                              }}
                            />
                          }
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            opacity: 0.6,
                            fontSize: '10px',
                            cursor: 'pointer'
                          }}
                        >
                          {todo.listName}
                        </Badge>
                      </UnstyledButton>
                    </Menu.Target>
                    <Menu.Dropdown>
                      {todoLists.map((listOption) => (
                        <Menu.Item
                          key={listOption.id}
                          leftSection={
                            <Box
                              style={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: listOption.color,
                              }}
                            />
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleListChange(listOption.id);
                          }}
                        >
                          {listOption.name}
                        </Menu.Item>
                      ))}
                    </Menu.Dropdown>
                  </Menu>
                )}
                {!hideListLabel && isPublicView && (
                  <Badge
                    variant="outline"
                    size="sm"
                    color="gray"
                    title={todo.listName}
                    leftSection={
                      <Box
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: listColor,
                          marginRight: 4
                        }}
                      />
                    }
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      opacity: 0.6,
                      fontSize: '10px'
                    }}
                  >
                    {todo.listName}
                  </Badge>
                )}
                
                {/* Status Badge for Kanban */}
                {todo.status && kanbanConfig && !isPublicView && (
                  (() => {
                    const statusColumn = kanbanConfig.columns?.find(col => col.id === todo.status);
                    if (!statusColumn || statusColumn.id === 'todo') return null; // Don't show for default 'todo' status
                    
                    return (
                      <Badge
                        variant="light"
                        size="sm"
                        style={{ 
                          backgroundColor: statusColumn.color ? `${statusColumn.color}15` : undefined,
                          color: statusColumn.color || 'var(--mantine-color-blue-6)',
                          borderColor: statusColumn.color || 'var(--mantine-color-blue-3)',
                          display: 'flex', 
                          alignItems: 'center'
                        }}
                      >
                        {statusColumn.name}
                      </Badge>
                    );
                  })()
                )}
                
                {/* Priority */}
                {(todo.priority === 'Low' || todo.priority === 'High') && !isPublicView && (
                  <Menu shadow="md" width={160}>
                    <Menu.Target>
                      <UnstyledButton
                        onClick={(e) => e.stopPropagation()}
                        style={{ display: 'flex', alignItems: 'center' }}
                      >
                        <Badge
                          variant="light"
                          color={
                            todo.priority === 'High' ? 'red' : 'gray'
                          }
                          size="sm"
                          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                        >
                          {todo.priority}
                        </Badge>
                      </UnstyledButton>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item
                        color="gray"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePriorityChange('Low');
                        }}
                      >
                        Low Priority
                      </Menu.Item>
                      <Menu.Item
                        color="orange"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePriorityChange('Medium');
                        }}
                      >
                        Medium Priority
                      </Menu.Item>
                      <Menu.Item
                        color="red"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePriorityChange('High');
                        }}
                      >
                        High Priority
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                )}
                {(todo.priority === 'Low' || todo.priority === 'High') && isPublicView && (
                  <Badge
                    variant="light"
                    color={
                      todo.priority === 'High' ? 'red' : 'gray'
                    }
                    size="sm"
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    {todo.priority}
                  </Badge>
                )}
                
                {/* Due Date */}
                {todo.dueDate && (
                  <div onClick={isPublicView ? undefined : handleDatePickerClick}>
                    <InlineDatePicker
                      value={todo.dueDate}
                      onChange={isPublicView ? () => {} : handleDateChange}
                      placeholder="Due Date"
                      disabled={isPublicView || updateMutation.isPending}
                    />
                  </div>
                )}
                
                {/* Sub-tasks Progress Indicator (minimal) */}
                {todo.totalSubTasksCount !== undefined && todo.totalSubTasksCount > 0 && (
                  <Tooltip
                    label={`${todo.completedSubTasksCount} of ${todo.totalSubTasksCount} sub-tasks completed`}
                    position="top"
                    withArrow
                  >
                    <Badge
                      variant="light"
                      size="sm"
                      color={todo.completedSubTasksCount === todo.totalSubTasksCount ? 'green' : 'gray'}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        fontSize: '10px',
                        opacity: 0.7
                      }}
                    >
                      ☑ {todo.completedSubTasksCount}/{todo.totalSubTasksCount}
                    </Badge>
                  </Tooltip>
                )}
                
                {/* Reminder Indicator */}
                {todo.reminderDateTime && (
                  <Tooltip
                    label={`Reminder: ${new Date(todo.reminderDateTime).toLocaleDateString()} at ${new Date(todo.reminderDateTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`}
                    position="top"
                    withArrow
                  >
                    <ActionIcon
                      variant="filled"
                      size="xs"
                      color="violet"
                      style={{ 
                        cursor: 'help',
                        backgroundColor: '#6B46C1'
                      }}
                    >
                      <Bell size={10} />
                    </ActionIcon>
                  </Tooltip>
                )}
                
                {/* Assignee */}
                {todo.assignedTo && todo.assignedTo !== '' && !isPublicView && (
                  <Menu shadow="md" width={200}>
                    <Menu.Target>
                      <UnstyledButton
                        onClick={(e) => e.stopPropagation()}
                        style={{ display: 'flex', alignItems: 'center' }}
                      >
                        <Badge
                          variant="light"
                          color="blue"
                          size="sm"
                          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                        >
                          {todo.assignedTo.split('@')[0]}
                        </Badge>
                      </UnstyledButton>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item
                        leftSection={<Avatar size={20} radius="sm">U</Avatar>}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAssigneeChange('__unassigned__');
                        }}
                      >
                        Unassigned
                      </Menu.Item>
                      {currentUser && (
                        <Menu.Item
                          leftSection={
                            <Avatar size={20} radius="sm" color="blue" src={currentUser.avatar || undefined}>
                              {!currentUser.avatar && currentUser.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                            </Avatar>
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAssigneeChange(currentUser.email);
                          }}
                        >
                          {currentUser.name} (Me)
                        </Menu.Item>
                      )}
                      {users.filter(user => !currentUser || user.email !== currentUser.email).map((user) => (
                        <Menu.Item
                          key={user.email}
                          leftSection={
                            <Avatar size={20} radius="sm" color="cyan" src={user.avatar || undefined}>
                              {!user.avatar && user.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                            </Avatar>
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAssigneeChange(user.email);
                          }}
                        >
                          {user.name}
                        </Menu.Item>
                      ))}
                    </Menu.Dropdown>
                  </Menu>
                )}
                {todo.assignedTo && todo.assignedTo !== '' && isPublicView && (
                  <Badge
                    variant="light"
                    color="blue"
                    size="sm"
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    {todo.assignedTo.split('@')[0]}
                  </Badge>
                )}
                
                {/* Comments */}
                {!isPublicView && localComments.length > 0 && (
                  <Comments
                    comments={localComments}
                    onAddComment={handleAddComment}
                    variant="inline"
                    searchTerm={searchTerm}
                  />
                )}
                
                {/* Completion Date */}
                {todo.isCompleted && todo.completedAt && (
                  <Badge 
                    variant="light" 
                    color="gray" 
                    size="sm"
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    {formatCompletionDate(todo.completedAt)}
                  </Badge>
                )}
              </Group>
            </Stack>


          </Flex>

        </Flex>
      </Card>
    </Box>
  );
};

export default TodoItem;
export { TodoItem as TodoItemComponent };
